import React, { ReactNode, useEffect, useLayoutEffect, useMemo } from 'react';
import './Table.less';
import {
    getCoreRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable,
    flexRender,
    Row,
    GroupingState,
    getGroupedRowModel,
    getExpandedRowModel,
    RowSelectionState,
    ColumnDef,
} from '@tanstack/react-table';
import { useVirtual } from 'react-virtual';
import { useTranslation } from 'react-i18next';
import Icon from '../Icon/Icon';
import classNames from 'classnames';
import { IndeterminateCheckbox } from '../IndeterminateCheckbox';

export const AggregateValue = ({ children }: { children: ReactNode }) => <span className={'italic opacity-60'}>{children}</span>;

export type SelectionGroupsMode = 'none' | 'deepest';

interface TableProps {
    className?: string;
    columns: ColumnDef<any>[];
    data: any[];
    selectable?: boolean;
    groupable?: boolean;
    empty?: ReactNode;
    fullWidth?: boolean;
    selectionGroups?: SelectionGroupsMode;
    onSelectionChange?: (ids: string[], rows: any[], groups: { name: string; data: any[] }[]) => void;
    rowSelection?: RowSelectionState;
    onRowSelectionChange?: (updater: RowSelectionState | ((old: RowSelectionState) => RowSelectionState)) => void;
    getRowId?: (row: any, index: number) => string;
}

const sanitizeSelectionGroupPart = (value: string) => value.replace(/[/\\:*?"<>|]/g, '').replace(/\s+/g, '_');

// TanStack legt Gruppierungswerte immer als String ab (`${row.getValue(columnId)}`),
// leere Werte landen deshalb als "null" bzw. "undefined" in der Gruppe — ein reines
// `?? ''` greift dort nie (#72).
const isEmptyGroupingValue = (value: unknown) => ['', 'null', 'undefined'].includes(String(value ?? ''));

// Anzahl der tatsächlichen Aufträge einer Gruppe. `row.subRows` enthält bei
// verschachtelter Gruppierung nur die nächste Ebene, `row.getLeafRows()` zählt die
// Zwischen-Gruppenzeilen mit — beides ergibt zu kleine bzw. zu große Zahlen (#72).
const getGroupJobCount = (row: Row<any>): number =>
    row.subRows.reduce((count, subRow) => count + (subRow.getIsGrouped() ? getGroupJobCount(subRow) : 1), 0);

const getRowSelectionSummary = (row: Row<any>, rowSelection: RowSelectionState): { total: number; selected: number } => {
    if (!row.subRows?.length) {
        return {
            total: row.getCanSelect() ? 1 : 0,
            selected: rowSelection[row.id] ? 1 : 0,
        };
    }

    return row.subRows.reduce(
        (summary, subRow) => {
            const subSummary = getRowSelectionSummary(subRow, rowSelection);

            return {
                total: summary.total + subSummary.total,
                selected: summary.selected + subSummary.selected,
            };
        },
        { total: 0, selected: 0 }
    );
};

const getGroupedRowSelectionState = (row: Row<any>, rowSelection: RowSelectionState) => {
    const selectionSummary = getRowSelectionSummary(row, rowSelection);
    const hasSelectedRows = selectionSummary.selected > 0;
    const hasAllSelectedRows = selectionSummary.total > 0 && selectionSummary.selected === selectionSummary.total;

    return {
        checked: hasAllSelectedRows,
        indeterminate: hasSelectedRows && !hasAllSelectedRows,
    };
};

const Table = ({
    className = undefined,
    columns,
    data,
    groupable = false,
    selectable = false,
    selectionGroups = 'none',
    onSelectionChange,
    fullWidth,
    rowSelection: controlledRowSelection,
    onRowSelectionChange,
    getRowId,
}: TableProps) => {
    const { t } = useTranslation();
    const emptyGroupingLabel = t('common.grouping.emptyValue');
    const tableContainerRef = React.useRef<HTMLDivElement>(null);
    const onSelectionChangeRef = React.useRef(onSelectionChange);

    const [sorting, setSorting] = React.useState<SortingState>([]);
    const [grouping, setGrouping] = React.useState<GroupingState>([]);
    const [internalRowSelection, setInternalRowSelection] = React.useState<RowSelectionState>({});
    const rowSelection = controlledRowSelection ?? internalRowSelection;
    const handleRowSelectionChange = onRowSelectionChange ?? setInternalRowSelection;

    useEffect(() => {
        onSelectionChangeRef.current = onSelectionChange;
    }, [onSelectionChange]);

    const table = useReactTable({
        data,
        columns: columns,
        state: {
            sorting,
            grouping,
            rowSelection,
        },
        enableGrouping: groupable,
        enableRowSelection: selectable,
        enableMultiRowSelection: selectable,
        enableSubRowSelection: selectable,
        onRowSelectionChange: handleRowSelectionChange,
        onGroupingChange: setGrouping,
        onSortingChange: setSorting,
        // Selektion an der stabilen Datensatz-ID verankern — der Index-Default
        // lässt Markierungen verrutschen, sobald sich die Liste ändert (#57).
        // Ein optionaler getRowId-Prop kann die Ableitung überschreiben.
        getRowId: getRowId ?? ((row: any, index: number) => (row?.id !== undefined && row?.id !== null ? String(row.id) : String(index))),
        getExpandedRowModel: getExpandedRowModel(),
        getGroupedRowModel: getGroupedRowModel(),
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        debugTable: false,
    });

    // Rausgefilterte Zeilen aus der Selektion entfernen (#313) — sonst bleibt die
    // zuletzt gemeldete Auswahl beim Aufrufer stehen und unsichtbare Zeilen landen
    // z.B. im Excel-Export. rowsById enthält Gruppen- und Leaf-Zeilen.
    useEffect(() => {
        handleRowSelectionChange((old) => {
            const { rowsById } = table.getGroupedRowModel();
            const next = Object.fromEntries(Object.entries(old).filter(([id]) => rowsById[id] !== undefined));

            return Object.keys(next).length === Object.keys(old).length ? old : next;
        });
    }, [data, table]);

    // Gruppen-Klick aus dem von den Kindern abgeleiteten Zustand entscheiden (#313):
    // tanstacks getToggleSelectedHandler prüft nur den eigenen rowSelection-Eintrag
    // der Gruppe. Der veraltet, sobald ein Kind einzeln getoggled wird, und der
    // Klick verpufft dann als No-Op. Deshalb werden Gruppen-IDs gar nicht mehr
    // persistiert, sondern nur die Leaf-Einträge gesetzt bzw. entfernt.
    const toggleGroupedRowSelection = (row: Row<any>) => {
        const nextSelected = !getGroupedRowSelectionState(row, rowSelection).checked;

        handleRowSelectionChange((old) => {
            const next = { ...old };

            delete next[row.id];

            row.getLeafRows().forEach((leaf) => {
                if (leaf.getIsGrouped() || !nextSelected) {
                    delete next[leaf.id];
                } else if (leaf.getCanSelect()) {
                    next[leaf.id] = true;
                }
            });

            return next;
        });
    };

    const { rows } = table.getRowModel();

    const { virtualItems: virtualRows, totalSize } = useVirtual({
        parentRef: tableContainerRef,
        size: rows.length,
        overscan: 10,
    });

    const paddingTop = useMemo(() => {
        return virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
    }, [virtualRows]);

    const paddingBottom = useMemo(() => {
        return virtualRows.length > 0 ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0) : 0;
    }, [virtualRows, totalSize]);

    useLayoutEffect(() => {
        if (tableContainerRef && tableContainerRef.current) {
            const parentHeight = tableContainerRef?.current?.parentElement?.clientHeight || 200;
            tableContainerRef.current.style.height = parentHeight + 'px';
        }
    });

    useEffect(() => {
        if (onSelectionChangeRef.current) {
            // Auswahl in sichtbarer (sortierter) Reihenfolge melden, damit z.B.
            // der Excel-Export der Sortierung der Tabelle folgt (#59).
            const selectedDisplayRows = table.getSortedRowModel().flatRows.filter((r) => !r.getIsGrouped() && r.getIsSelected());
            const selectedJobIds = selectedDisplayRows.map((r) => r.original.id);
            const selectedJobs = selectedDisplayRows.map((r) => r.original);
            const groups: { name: string; data: any[] }[] = [];

            if (selectionGroups === 'deepest' && grouping.length > 0) {
                const getGroupNamePart = (row: Row<any>) => {
                    const groupingValue = (row as any).groupingValue;
                    const value = isEmptyGroupingValue(groupingValue) ? emptyGroupingLabel : String(groupingValue);

                    // Nur der Wert selbst — die Spaltenüberschrift steckt meist schon im
                    // Wert ("Straße_Odermannstraße 11") und blähte den Dateinamen auf (#72).
                    return sanitizeSelectionGroupPart(value);
                };

                // Blätter über `subRows` des sortierten Modells sammeln: `getLeafRows()` liefert
                // seit @tanstack/react-table 8.x die Datenreihenfolge statt der Sortierung (#59).
                const getSortedLeafRows = (row: Row<any>): Row<any>[] =>
                    row.subRows.flatMap((subRow) => (subRow.getIsGrouped() ? getSortedLeafRows(subRow) : [subRow]));

                const traverseGroups = (rows: Row<any>[], parentParts: string[] = []) => {
                    for (const row of rows) {
                        if (!row.getIsGrouped()) {
                            continue;
                        }

                        const parts = [...parentParts, getGroupNamePart(row)];
                        const leaves = Array.from(
                            new Map(getSortedLeafRows(row).filter((leaf) => leaf.getIsSelected()).map((leaf) => [leaf.original.id, leaf])).values()
                        );

                        if (leaves.length === 0) {
                            continue;
                        }

                        // Immer bis zur feinsten Gruppierungsebene absteigen — auch bei
                        // vollständig ausgewählter Obergruppe, sonst entsteht beim Export
                        // eine Sammeldatei statt einer Datei pro Untergruppe (#61).
                        const groupedSubRows = row.subRows.filter((subRow) => subRow.getIsGrouped());

                        if (groupedSubRows.length > 0) {
                            traverseGroups(groupedSubRows, parts);
                            continue;
                        }

                        groups.push({
                            name: parts.join('-'),
                            data: leaves.map((leaf) => leaf.original),
                        });
                    }
                };

                traverseGroups(table.getSortedRowModel().rows);
            }

            onSelectionChangeRef.current(selectedJobIds, selectedJobs, groups);
        }
    }, [rowSelection, grouping, sorting, selectionGroups, table, emptyGroupingLabel]);

    return (
        <div className={classNames('envago-table', className)}>
            <div ref={tableContainerRef} className={classNames('table-container', { 'full-width': fullWidth })}>
                <table>
                    <thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    return (
                                        <th
                                            key={header.id}
                                            colSpan={header.colSpan}
                                            className={classNames({
                                                'font-bold': true,
                                            })}
                                            style={{ width: header.colSpan > 1 ? undefined : header.getSize() !== 150 ? header.getSize() : undefined }}
                                        >
                                            {header.isPlaceholder ? null : (
                                                <div
                                                    {...{
                                                        className: header.column.getCanSort() ? 'cursor-pointer select-none flex items-center gap-1' : '',
                                                        onClick: header.column.getToggleSortingHandler(),
                                                    }}
                                                >
                                                    {header.column.getCanGroup() ? (
                                                        // If the header can be grouped, let's add a toggle
                                                        <button
                                                            {...{
                                                                onClick: header.column.getToggleGroupingHandler(),
                                                                style: {
                                                                    cursor: 'pointer',
                                                                },
                                                            }}
                                                            aria-label={''}
                                                        >
                                                            {header.column.getIsGrouped() ? (
                                                                <Icon aria={{'aria-label': 'Gruppierung aufheben Icon'}} className={'h-4 text-accent'} path={Icon.Path.mdiSelectGroup} />
                                                            ) : (
                                                                <Icon aria={{'aria-label': 'Gruppierung Icon'}} className={'h-4 text-accent'} path={Icon.Path.mdiSelectGroup} />
                                                            )}
                                                        </button>
                                                    ) : null}
                                                    <div>{flexRender(header.column.columnDef.header, header.getContext())}</div>
                                                    <div>
                                                        {{
                                                            asc: <Icon aria={{'aria-label': 'Sortierung Aufsteigend Icon'}} className={'h-4'} path={Icon.Path.mdiSortAscending} />,
                                                            desc: <Icon aria={{'aria-label': 'Sortierung Absteigend Icon'}} className={'h-4'} path={Icon.Path.mdiSortDescending} />,
                                                        }[header.column.getIsSorted() as string] ?? null}
                                                    </div>
                                                </div>
                                            )}
                                        </th>
                                    );
                                })}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {paddingTop > 0 && (
                            <tr>
                                <td style={{ height: `${paddingTop}px` }} />
                            </tr>
                        )}
                        {virtualRows.map((virtualRow) => {
                            const row = rows[virtualRow.index] as Row<any>;

                            return (
                                <tr
                                    key={row.id}
                                    style={{
                                        minHeight: 40,
                                    }}
                                >
                                    {row.getVisibleCells().map((cell) => {
                                        return (
                                            <td
                                                key={cell.id}
                                                {...{
                                                    className: classNames({
                                                        'whitespace-nowrap': true,
                                                        flex: cell.getIsGrouped(),
                                                        'bg-gray-100': cell.getIsGrouped() || cell.getIsPlaceholder(),
                                                    }),
                                                    style: {},
                                                }}
                                            >
                                                {cell.getIsGrouped() ? (
                                                    // If it's a grouped cell, add an expander and row count
                                                    <div className={'flex items-center gap-1'}>
                                                        <IndeterminateCheckbox
                                                            {...{
                                                                ...getGroupedRowSelectionState(row, rowSelection),
                                                                onChange: () => toggleGroupedRowSelection(row),
                                                            }}
                                                            aria-label={'Selektion'}
                                                        />
                                                        <button
                                                            className={classNames({
                                                                'ml-2': true,
                                                                flex: true,
                                                                'items-center': true,
                                                            })}
                                                            {...{
                                                                onClick: row.getToggleExpandedHandler(),
                                                                style: {
                                                                    cursor: row.getCanExpand() ? 'pointer' : 'normal',
                                                                },
                                                            }}
                                                        >
                                                            <div>
                                                                {row.getIsExpanded() ? (
                                                                    <Icon aria={{'aria-label':'Gruppierung einklappen'}} className={'h-4 mr-1'} path={Icon.Path.mdiMinusBoxOutline} />
                                                                ) : (
                                                                    <Icon aria={{'aria-label':'Gruppierung ausklappen'}} className={'h-4 mr-1'} path={Icon.Path.mdiPlusBoxOutline} />
                                                                )}{' '}
                                                            </div>
                                                            <div>
                                                                {isEmptyGroupingValue((row as any).groupingValue)
                                                                    ? emptyGroupingLabel
                                                                    : flexRender(cell.column.columnDef.cell, cell.getContext())}{' '}
                                                                ({getGroupJobCount(row)})
                                                            </div>
                                                        </button>
                                                    </div>
                                                ) : cell.getIsAggregated() ? (
                                                    // If the cell is aggregated, use the Aggregated
                                                    // renderer for cell
                                                    <div>
                                                        {flexRender(cell.column.columnDef.aggregatedCell ?? cell.column.columnDef.cell, cell.getContext())}
                                                    </div>
                                                ) : cell.getIsPlaceholder() ? null : ( // For cells with repeated values, render null
                                                    // Otherwise, just render the regular cell
                                                    <div>{flexRender(cell.column.columnDef.cell, cell.getContext())}</div>
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                        {paddingBottom > 0 && (
                            <tr>
                                <td style={{ height: `${paddingBottom}px` }} />
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default Table;
